import { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import IPButton from '../../../../components/IPButton'
import IPInput from '../../../../components/IPInput'
import IPModal from '../../../../components/IPModal'
import { BaseComponentWithContextProps } from '../../../../base/customHooks/componentHOC'
import { IException } from '../../../../base/models'
import { IAppleDepCustomer, IAppleDepEnvironment } from '../models/models'
import {
  DeleteAppleDepCustomerApi,
  GetAppleDepCustomersApi,
  SaveAppleDepCustomerApi,
  SaveMyAppleDepOrganizationApi,
} from '../services/appleDepApi'

interface CustomersTabProps extends BaseComponentWithContextProps {
  environment?: IAppleDepEnvironment
  onCustomersChanged: () => void
  /** Kendi kurum bilgisi değişince ortam bilgisini tazelemek için. */
  onEnvironmentChanged?: () => void
}

const emptyCustomer = (): IAppleDepCustomer => ({
  Id: 0,
  CustomerName: '',
  OrganizationId: '',
  TaxNumber: '',
  ContactName: '',
  ContactEmail: '',
  ContactPhone: '',
  Active: true,
  DeviceCount: 0,
})

/**
 * Son kullanıcı (kurumsal müşteri) master'ı. Apple, Organization ID'nin her
 * işlemde elle girilmemesini şart koştuğu için müşteri burada bir kez tanımlanır,
 * cihaz gönderiminde listeden seçilir.
 */
export default function CustomersTab(props: CustomersTabProps) {
  // Yalnızca kendi kurumu modunda son kullanıcı tanımları gönderimde kullanılmaz.
  const onlyDealerOwn =
    (props.environment?.CanUseDealerOwnOrganization ?? false) &&
    !(props.environment?.CanUseEndCustomerOrganization ?? true)

  // Mod izin veriyorsa bayi kendi Organization ID'sini girebilir (henüz girmemiş olsa da).
  const dealerOwnAllowed = props.environment?.DealerOwnAllowedByMode ?? false

  // Gönderimler Index'in tek Reseller ID'si ile yapılır; son kullanıcının bunu
  // ABM'de yetkili tedarikçi olarak eklemesi Apple'ın şartı.
  const resellerId = props.environment?.DealerResellerId

  const [showOwnModal, setShowOwnModal] = useState(false)
  const [ownOrganizationId, setOwnOrganizationId] = useState('')
  const [ownOrganizationName, setOwnOrganizationName] = useState('')

  const [customers, setCustomers] = useState<IAppleDepCustomer[]>([])
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<IAppleDepCustomer>(emptyCustomer())

  const loadCustomers = async () => {
    props.showLoading()
    try {
      const api: GetAppleDepCustomersApi = new GetAppleDepCustomersApi({})
      setCustomers(await api.getCustomers())
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  useEffect(() => {
    loadCustomers()
    // İlk yüklemede bir kez çalışır; sonraki yenilemeler kaydet/sil sonrası tetiklenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showError = (err: any) => {
    const exception = err as IException
    Swal.fire({
      icon: 'error',
      title: exception?.description ?? 'İşlem sırasında bir hata oluştu.',
      showConfirmButton: true,
      allowOutsideClick: false,
    })
  }

  const openNew = () => {
    setEditing(emptyCustomer())
    setShowModal(true)
  }

  const openOwnModal = () => {
    setOwnOrganizationId(props.environment?.DealerOwnOrganizationId ?? '')
    setOwnOrganizationName(props.environment?.DealerOwnOrganizationName ?? '')
    setShowOwnModal(true)
  }

  const saveOwnOrganization = async () => {
    if (!ownOrganizationId.trim()) {
      Swal.fire({ icon: 'warning', title: 'Organization ID zorunludur.' })
      return
    }

    props.showLoading()
    try {
      const api: SaveMyAppleDepOrganizationApi = new SaveMyAppleDepOrganizationApi({})
      await api.saveMyOrganization({
        OrganizationId: ownOrganizationId,
        OrganizationName: ownOrganizationName,
      })

      setShowOwnModal(false)
      await loadCustomers()
      props.onCustomersChanged()
      // Atama ekranındaki "kendi kurumum" seçeneği ortam bilgisinden geliyor.
      props.onEnvironmentChanged?.()

      Swal.fire({ icon: 'success', title: 'Kurum bilginiz kaydedildi.', timer: 1500, showConfirmButton: false })
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  const openEdit = (customer: IAppleDepCustomer) => {
    setEditing({ ...customer })
    setShowModal(true)
  }

  const saveCustomer = async () => {
    if (!editing.CustomerName?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Müşteri adı zorunludur.' })
      return
    }

    if (!editing.OrganizationId?.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Organization ID zorunludur.',
        text: 'Müşteriniz bu numarayı business.apple.com → Settings → Enrollment Information ekranından alabilir.',
      })
      return
    }

    props.showLoading()
    try {
      const api: SaveAppleDepCustomerApi = new SaveAppleDepCustomerApi({})
      await api.saveCustomer(editing)
      setShowModal(false)
      await loadCustomers()
      props.onCustomersChanged()
      Swal.fire({ icon: 'success', title: 'Müşteri kaydedildi.', timer: 1500, showConfirmButton: false })
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  const deleteCustomer = async (customer: IAppleDepCustomer) => {
    const confirmation = await Swal.fire({
      icon: 'question',
      title: 'Müşteri silinsin mi?',
      text: `${customer.CustomerName} (${customer.OrganizationId})`,
      showCancelButton: true,
      confirmButtonText: 'Sil',
      cancelButtonText: 'Vazgeç',
    })

    if (!confirmation.isConfirmed) return

    props.showLoading()
    try {
      const api: DeleteAppleDepCustomerApi = new DeleteAppleDepCustomerApi({})
      await api.deleteCustomer(customer.Id)
      await loadCustomers()
      props.onCustomersChanged()
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  return (
    <div className='appleDepCustomers'>
      {/* Apple'ın chain-of-custody şartı: müşteri, gönderimi yapan Reseller ID'yi
          ABM'de yetkili tedarikçi olarak eklemeden cihazlar hesabına düşmez. */}
      <div className='appleDepSupplierNotice'>
        <div className='appleDepSupplierNoticeTitle'>
          Müşterinizin yapması gereken tek şey
        </div>
        <p>
          Cihazların müşterinizin Apple Business Manager hesabına düşebilmesi için, müşterinizin{' '}
          <strong>Index'in Reseller ID'sini{resellerId ? ` (${resellerId})` : ''}</strong> yetkili
          tedarikçi olarak eklemesi gerekir:
        </p>
        <p className='appleDepSupplierPath'>
          business.apple.com → <strong>Settings</strong> → <strong>Device Management Settings</strong>{' '}
          → yeşil <strong>+</strong> → Reseller ID{resellerId ? `: ${resellerId}` : ''}
        </p>
        <p className='appleDepSupplierWarn'>
          Bu adım yapılmadan gönderim Apple tarafından reddedilir.
        </p>
      </div>

      {onlyDealerOwn && (
        <div className='appleDepWarning'>
          Bu bayide cihazlar yalnızca <strong>kendi kurumunuzun</strong> Apple Business Manager
          hesabına kaydediliyor. Son kullanıcı müşteri tanımları gönderimde kullanılmaz.
        </div>
      )}

      {dealerOwnAllowed && (
        <div className='appleDepOwnOrgCard'>
          <div>
            <div className='appleDepOwnOrgTitle'>Kendi Kurumum</div>
            {props.environment?.DealerOwnOrganizationId ? (
              <div className='appleDepOwnOrgValue'>
                <strong>{props.environment.DealerOwnOrganizationId}</strong>
                {props.environment.DealerOwnOrganizationName
                  ? ` — ${props.environment.DealerOwnOrganizationName}`
                  : ''}
              </div>
            ) : (
              <div className='appleDepOwnOrgMissing'>
                Kurumunuzun Apple Business Manager Organization ID bilgisi henüz girilmemiş.
                Cihazları kendi hesabınıza kaydedebilmek için girmeniz gerekiyor.
              </div>
            )}
            <div className='appleDepHint'>
              business.apple.com → Settings → Enrollment Information ekranından alabilirsiniz.
            </div>
          </div>
          <IPButton
            text={props.environment?.DealerOwnOrganizationId ? 'Düzenle' : 'Organization ID Gir'}
            className='appleDepSecondaryButton'
            onClick={openOwnModal}
          />
        </div>
      )}

      <div className='appleDepToolbar'>
        <div className='appleDepToolbarInfo'>
          Apple, cihaz kaydında müşterinin <strong>Organization ID</strong>'sinin her işlemde elle
          girilmesine izin vermiyor. Müşterilerinizi burada bir kez tanımlayın, gönderim sırasında listeden seçin.
        </div>
        <IPButton text='Yeni Müşteri' className='appleDepPrimaryButton' onClick={openNew} />
      </div>

      {customers.length === 0 ? (
        <div className='appleDepEmpty'>Henüz müşteri tanımlamadınız.</div>
      ) : (
        <div className='appleDepTableWrapper'>
          <table className='table table-hover appleDepTable'>
            <thead>
              <tr>
                <th>Müşteri Adı</th>
                <th>Organization ID</th>
                <th>Vergi No</th>
                <th>Yetkili</th>
                <th>E-posta</th>
                <th>Telefon</th>
                <th>Cihaz</th>
                <th>Durum</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customers.map(customer => (
                <tr key={customer.Id}>
                  <td>
                    {customer.CustomerName}
                    {customer.IsDealerOwn && (
                      <span className='appleDepBadge appleDepBadgeReady appleDepOwnBadge'>Kendi kurumum</span>
                    )}
                  </td>
                  <td>
                    <strong>{customer.OrganizationId}</strong>
                  </td>
                  <td>{customer.TaxNumber}</td>
                  <td>{customer.ContactName}</td>
                  <td>{customer.ContactEmail}</td>
                  <td>{customer.ContactPhone}</td>
                  <td>{customer.DeviceCount}</td>
                  <td>
                    <span
                      className={
                        customer.Active
                          ? 'appleDepBadge appleDepBadgeSuccess'
                          : 'appleDepBadge appleDepBadgeDefault'
                      }
                    >
                      {customer.Active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className='appleDepRowActions'>
                    {customer.IsDealerOwn ? (
                      // Kendi kurumu satırı ayar kaydından türer; düzenleme oraya gider.
                      <button type='button' className='appleDepLinkButton' onClick={openOwnModal}>
                        Düzenle
                      </button>
                    ) : (
                      <>
                        <button type='button' className='appleDepLinkButton' onClick={() => openEdit(customer)}>
                          Düzenle
                        </button>
                        <button
                          type='button'
                          className='appleDepLinkButtonDanger'
                          onClick={() => deleteCustomer(customer)}
                        >
                          Sil
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <IPModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={saveCustomer}
        confirmText='Kaydet'
        width={720}
        title={editing.Id > 0 ? 'Müşteri Düzenle' : 'Yeni Müşteri'}
      >
        <div className='appleDepForm'>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Müşteri Adı *</label>
              <IPInput
                type='text'
                value={editing.CustomerName}
                onChange={e => setEditing({ ...editing, CustomerName: e.target.value })}
              />
            </div>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Organization ID *</label>
              <IPInput
                type='text'
                maxLength={32}
                value={editing.OrganizationId}
                onChange={e => setEditing({ ...editing, OrganizationId: e.target.value })}
              />
              <small className='appleDepHint'>
                Müşteri: business.apple.com → Settings → Enrollment Information
              </small>
            </div>
          </div>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Vergi No</label>
              <IPInput
                type='text'
                maxLength={20}
                value={editing.TaxNumber}
                onChange={e => setEditing({ ...editing, TaxNumber: e.target.value })}
              />
            </div>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Yetkili Adı</label>
              <IPInput
                type='text'
                maxLength={100}
                value={editing.ContactName}
                onChange={e => setEditing({ ...editing, ContactName: e.target.value })}
              />
            </div>
          </div>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label className='labeltext'>E-posta</label>
              <IPInput
                type='email'
                maxLength={150}
                value={editing.ContactEmail}
                onChange={e => setEditing({ ...editing, ContactEmail: e.target.value })}
              />
            </div>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Telefon</label>
              <IPInput
                type='text'
                maxLength={30}
                value={editing.ContactPhone}
                onChange={e => setEditing({ ...editing, ContactPhone: e.target.value })}
              />
            </div>
          </div>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label className='labeltext'>
                <input
                  type='checkbox'
                  checked={editing.Active}
                  onChange={e => setEditing({ ...editing, Active: e.target.checked })}
                />{' '}
                Aktif
              </label>
            </div>
          </div>

          <div className='appleDepInfoBox'>
            Bu müşterinin Apple Business Manager hesabında{' '}
            <strong>Index'in Reseller ID'sini{resellerId ? ` (${resellerId})` : ''}</strong> yetkili
            tedarikçi olarak eklemesi gerekir: <em>Settings → Device Management Settings</em>.
            Eklenmezse Apple gönderimi reddeder.
          </div>
        </div>
      </IPModal>

      <IPModal
        show={showOwnModal}
        onClose={() => setShowOwnModal(false)}
        onConfirm={saveOwnOrganization}
        confirmText='Kaydet'
        width={640}
        title='Kendi Kurumumun Apple Business Manager Bilgisi'
      >
        <div className='appleDepForm'>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Organization ID *</label>
              <IPInput
                type='text'
                maxLength={32}
                value={ownOrganizationId}
                onChange={e => setOwnOrganizationId(e.target.value)}
              />
              <small className='appleDepHint'>
                business.apple.com → Settings → Enrollment Information
              </small>
            </div>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Kurum Adı</label>
              <IPInput
                type='text'
                maxLength={255}
                value={ownOrganizationName}
                onChange={e => setOwnOrganizationName(e.target.value)}
              />
              <small className='appleDepHint'>Müşteri listesinde bu adla görünür</small>
            </div>
          </div>

          <div className='appleDepInfoBox'>
            Bu bilgi, cihazları <strong>kendi</strong> Apple Business Manager hesabınıza
            kaydetmek için kullanılır. Cihazların hesabınıza düşebilmesi için Index'in Reseller
            ID'sini ABM'de <strong>yetkili tedarikçi</strong> olarak eklemiş olmanız gerekir.
          </div>
        </div>
      </IPModal>
    </div>
  )
}
